import React, { SFC, useEffect, useCallback } from "react";

import "./style.scss";

/**
 * Props interface for the Modal component
 * @interface ModalProps
 * @property {string | string[]} text - Text content to display in the modal. Can be a single string or array of strings for multiple paragraphs
 * @property {string} [className] - Optional CSS class for styling
 * @property {string[]} [visibleTo] - Optional array of user roles that can see this modal
 * @property {() => void} onClose - Callback function called when modal is closed
 */
export interface ModalProps {
    text: string | string[];
    className?: string;
    visibleTo?: string[];
    onClose: () => void;
}

/**
 * A modal dialog component that displays text content in an overlay.
 * Can be dismissed by clicking anywhere, pressing Enter, or pressing Escape.
 * Adds a 'static' class to the body when mounted to prevent background interaction.
 * 
 * @component
 * @example
 * ```tsx
 * <Modal 
 *   text={["First paragraph", "Second paragraph"]}
 *   className="custom-modal"
 *   onClose={() => console.log('Modal closed')}
 * />
 * ```
 */
const Modal: SFC<ModalProps> = (props) => {
    const { text, className, onClose } = props;
    const css = [
        "__modal__",
        className ? className : null,
    ].join(" ").trim();

    /**
     * Renders the modal content, converting single string to array
     * and mapping each string to a paragraph element
     * @returns {JSX.Element[]} Array of paragraph elements
     */
    const renderContent = () => {
        const content = (typeof text === "string") ? [text] : text;
        return content.map((element, index) => <p key={index}>{element}</p>);
    }

    /**
     * Handles keyboard events for dismissing the modal
     * @param {KeyboardEvent} e - The keyboard event
     */
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        e.preventDefault();

        const key = e.key.toLowerCase();

        switch (key) {
            case "enter":
            case "escape":
                onClose && onClose();
                break;

            default:
                break;
        }
    }, [onClose]);

    /**
     * Sets up event listeners and body class on mount,
     * cleans up on unmount
     */
    useEffect(() => {
        // mount
        document.body.classList.add("static");
        document.addEventListener("keydown", handleKeyDown);

        // unmount
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.classList.remove("static");
        };
    });

    return (
        <section className={css} onClick={onClose}>
            <div className="content">
                {renderContent()}
            </div>
        </section>
    );
};

export default Modal;
